import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { IoNotificationsOutline } from "react-icons/io5";
import { IoClose } from "react-icons/io5";

const DISMISS_STORAGE_KEY = "shotkut-staff-embedding-alert-dismissed-at-count";

const StaffEmbeddingAlertContext = createContext(null);

export function useStaffEmbeddingAlert() {
  return useContext(StaffEmbeddingAlertContext);
}

export function StaffEmbeddingAlertProvider({ auth, children }) {
  const [unembeddedCount, setUnembeddedCount] = useState(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const isStaff =
    auth?.role === "ADMIN" || auth?.role === "EMPLOYEE";

  const refreshCount = useCallback(async () => {
    if (!isStaff || !auth?.RawToken) {
      setUnembeddedCount(null);
      setPanelVisible(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/search/unembedded-count`,
        {
          headers: { Authorization: `Bearer ${auth.RawToken}` },
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        },
      );

      if (response.status === 204) {
        setUnembeddedCount(null);
        setPanelVisible(false);
        return;
      }

      const count =
        response.data?.missingFromVectorSearchCount ??
        response.data?.unembeddedCount;

      if (typeof count !== "number" || count <= 0) {
        setUnembeddedCount(null);
        setPanelVisible(false);
        return;
      }

      setUnembeddedCount(count);

      let dismissedAt = null;
      try {
        dismissedAt = sessionStorage.getItem(DISMISS_STORAGE_KEY);
      } catch {
        dismissedAt = null;
      }

      const dismissedCount = dismissedAt ? Number(dismissedAt) : null;
      setPanelVisible(dismissedCount !== count);
    } catch (error) {
      console.error("Unembedded video count check failed:", error);
      setUnembeddedCount(null);
      setPanelVisible(false);
    } finally {
      setLoading(false);
    }
  }, [auth?.RawToken, isStaff]);

  useEffect(() => {
    if (!isStaff || !auth?.RawToken) {
      setUnembeddedCount(null);
      setPanelVisible(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      await refreshCount();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
  }, [auth?.RawToken, isStaff, refreshCount]);

  const openPanel = useCallback(() => {
    setPanelVisible(true);
  }, []);

  const dismissPanel = useCallback(() => {
    if (unembeddedCount != null) {
      try {
        sessionStorage.setItem(
          DISMISS_STORAGE_KEY,
          String(unembeddedCount),
        );
      } catch {
        /* ignore */
      }
    }
    setPanelVisible(false);
  }, [unembeddedCount]);

  const handleRefresh = useCallback(() => {
    setPanelVisible(true);
    refreshCount();
  }, [refreshCount]);

  const value = useMemo(
    () => ({
      unembeddedCount,
      panelVisible,
      loading,
      openPanel,
      dismissPanel,
      refreshCount: handleRefresh,
    }),
    [unembeddedCount, panelVisible, loading, openPanel, dismissPanel, handleRefresh],
  );

  const showBanner =
    isStaff && unembeddedCount != null && panelVisible;

  return (
    <StaffEmbeddingAlertContext.Provider value={value}>
      {children}
      {showBanner &&
        createPortal(
          <div
            className="fixed right-3 sm:right-5 z-[300] pt-3 w-[min(calc(100vw-1.5rem),20rem)]"
            style={{ top: "58px" }}
            role="alert"
            aria-labelledby="staff-embedding-alert-title"
            aria-describedby="staff-embedding-alert-body"
          >
            <div className="w-full rounded-xl border-2 border-amber-500 bg-amber-50 text-amber-950 shadow-[0_12px_40px_rgba(245,158,11,0.35)] ring-1 ring-amber-200/80 dark:border-amber-400 dark:bg-[#3d2a06] dark:text-amber-50 dark:ring-amber-600/50 dark:shadow-[0_12px_40px_rgba(0,0,0,0.55)]">
              <div className="flex items-start gap-3 p-4 sm:p-5">
                <div className="mt-0.5 shrink-0 rounded-full bg-amber-500 p-2 text-black dark:bg-amber-400">
                  <IoNotificationsOutline className="h-5 w-5" aria-hidden />
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p
                    id="staff-embedding-alert-title"
                    className="text-base font-bold text-amber-950 dark:text-amber-50"
                  >
                    Search indexing
                  </p>
                  <p
                    id="staff-embedding-alert-body"
                    className="mt-1 text-sm leading-relaxed text-amber-900 dark:text-amber-100"
                  >
                    {unembeddedCount} video{unembeddedCount === 1 ? "" : "s"}{" "}
                    {unembeddedCount === 1 ? "has" : "have"} not been embedded
                    yet, so {unembeddedCount === 1 ? "it will" : "they will"}{" "}
                    not show up as well in search.
                  </p>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleRefresh}
                    className="mt-3 text-xs font-semibold rounded-md px-2.5 py-1.5 border border-amber-600/60 bg-amber-100/80 text-amber-950 hover:bg-amber-200/90 disabled:opacity-50 dark:bg-amber-900/40 dark:text-amber-50 dark:border-amber-500/50 dark:hover:bg-amber-900/70"
                  >
                    {loading ? "Refreshing…" : "Refresh count"}
                  </button>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg p-2 text-amber-800 hover:bg-amber-200/80 dark:text-amber-100 dark:hover:bg-amber-900/60"
                  aria-label="Dismiss search indexing alert"
                  onClick={dismissPanel}
                >
                  <IoClose className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </StaffEmbeddingAlertContext.Provider>
  );
}

export function StaffEmbeddingAlertBell() {
  const ctx = useStaffEmbeddingAlert();
  const count = ctx?.unembeddedCount;

  if (count == null || count <= 0) {
    return null;
  }

  return (
    <button
      type="button"
      className="relative cursor-pointer text-[var(--primary)] hover:text-[var(--hover)] transition-all duration-250 p-1"
      aria-label={`${count} videos not embedded for search. Open alert.`}
      onClick={() => ctx?.openPanel?.()}
    >
      <IoNotificationsOutline className="text-2xl" />
      <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
        {count > 99 ? "99+" : count}
      </span>
    </button>
  );
}
